const BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";
const TOKEN_KEY = "careos.access_token";
const REFRESH_KEY = "careos.refresh_token";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const getRefresh = () => localStorage.getItem(REFRESH_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export function setSession(access, refresh) {
  if (access) localStorage.setItem(TOKEN_KEY, access);
  if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
}
export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

export class ApiError extends Error {
  constructor(status, message) {
    super(message);
    this.status = status;
  }
}

async function tryRefresh() {
  const refresh = getRefresh();
  if (!refresh) return false;
  try {
    const res = await fetch(`${BASE}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refresh }),
    });
    if (!res.ok) return false;
    const data = await res.json();
    if (data?.access_token) {
      setSession(data.access_token, data.refresh_token);
      return true;
    }
  } catch {}
  return false;
}

async function request(path, opts = {}, auth = true, _retried = false) {
  const headers = { "Content-Type": "application/json", ...(opts.headers || {}) };
  if (auth) {
    const token = getToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;
  }
  const res = await fetch(`${BASE}${path}`, { ...opts, headers });
  if (res.status === 401 && auth && !_retried && !path.startsWith("/auth/")) {
    if (await tryRefresh()) return request(path, opts, auth, true);
    clearToken();
  }
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }
  if (!res.ok) {
    const detail = data?.detail;
    const msg =
      typeof detail === "string"
        ? detail
        : Array.isArray(detail)
        ? detail[0]?.msg || "Request failed"
        : "Request failed";
    throw new ApiError(res.status, msg);
  }
  return data;
}

export const api = {
  get: (p, auth = true) => request(p, { method: "GET" }, auth),
  post: (p, body, auth = true) =>
    request(p, { method: "POST", body: body ? JSON.stringify(body) : undefined }, auth),
  patch: (p, body, auth = true) =>
    request(p, { method: "PATCH", body: body ? JSON.stringify(body) : undefined }, auth),
  put: (p, body, auth = true) =>
    request(p, { method: "PUT", body: body ? JSON.stringify(body) : undefined }, auth),
  raw: BASE,
};

/** Upload a File via multipart FormData to the documents endpoint. */
export async function uploadDocument(docType, file, expiresOn) {
  const form = new FormData();
  form.append("file", file, file.name || `${docType}.jpg`);
  form.append("doc_type", docType);
  if (expiresOn) form.append("expires_on", expiresOn);
  const token = getToken();
  const res = await fetch(`${BASE}/documents/upload`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: form,
  });
  if (!res.ok) throw new Error((await res.text()) || "Upload failed");
  return res.json();
}

export async function documentViewUrl(docId) {
  const token = getToken();
  const res = await fetch(`${BASE}/documents/link/${docId}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!res.ok) throw new Error("Could not create a link");
  const data = await res.json();
  return `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}${data.path}`;
}
